/**
 * TimesheetsSection — feuilles de temps (périodes de paie) côté client (Batch #14.251).
 * Liste + création (dates suggérées) ; ouverture d'une période → TimesheetDetail.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CalendarClock, ChevronRight, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, portalHeaders, Field, SectionCard, inputCls, isDemoResponse, addBtnCls, formActionsCls, cancelBtnCls, saveBtnCls, deleteBtnCls,
  FREQ_LABELS, YEARS, ClientStatus, nextPeriod, fmtDate, err } from "./_shared";
import TimesheetDetail from "./TimesheetDetail";

function RunForm({ runs, defaultFrequency, primaryColor, onDone, onCancel }) {
  const freq0 = defaultFrequency || "biweekly";
  const [f, setF] = useState({ frequency: freq0, note: "", ...nextPeriod(runs, freq0) });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setFreq = (v) => setF((x) => ({ ...x, frequency: v, ...nextPeriod(runs, v) }));
  const submit = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/portal/paie/runs`, f, { headers: portalHeaders() });
      if (!isDemoResponse(res)) toast.success(`Feuille de temps créée — ${res.data.nb_employees} employé(s)`);
      onDone(res.data);
    } catch (e) { err(e); } finally { setSaving(false); }
  };
  return (
    <div className="border-2 rounded-2xl p-4 space-y-3 bg-gray-50/60" style={{ borderColor: primaryColor }} data-testid="paie-portal-run-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Fréquence de paie">
          <select className={inputCls} value={f.frequency} onChange={(e) => setFreq(e.target.value)} data-testid="paie-portal-run-frequency">
            {Object.entries(FREQ_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>
        <Field label="Date de paie"><input type="date" className={inputCls} value={f.pay_date} onChange={(e) => set("pay_date", e.target.value)} data-testid="paie-portal-run-pay-date" /></Field>
        <Field label="Début de période"><input type="date" className={inputCls} value={f.period_start} onChange={(e) => set("period_start", e.target.value)} data-testid="paie-portal-run-start" /></Field>
        <Field label="Fin de période"><input type="date" className={inputCls} value={f.period_end} onChange={(e) => set("period_end", e.target.value)} data-testid="paie-portal-run-end" /></Field>
        <Field label="Note spéciale pour cette paie" className="sm:col-span-2"><input className={inputCls} value={f.note} onChange={(e) => set("note", e.target.value)} placeholder="Ex. : prime de Noël pour tous, nouvel employé…" data-testid="paie-portal-run-note" /></Field>
      </div>
      <p className="text-[11px] text-gray-500">Tous les employés actifs payés à cette fréquence seront ajoutés avec leurs heures habituelles — vous pourrez les ajuster.</p>
      <div className={formActionsCls}>
        <button type="button" onClick={onCancel} className={cancelBtnCls}>Annuler</button>
        <button type="button" onClick={submit} disabled={saving} className={saveBtnCls} style={{ backgroundColor: primaryColor }} data-testid="paie-portal-run-create">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Créer la feuille de temps
        </button>
      </div>
    </div>
  );
}

export default function TimesheetsSection({ summary, primaryColor, onChanged }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [runs, setRuns] = useState(null);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/portal/paie/runs?year=${year}`, { headers: portalHeaders() });
      setRuns(r.data.items || []);
    } catch (e) { err(e, "Erreur chargement des feuilles de temps"); setRuns([]); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const remove = async (r) => {
    if (!window.confirm(`Supprimer la feuille de temps du ${r.period_start} au ${r.period_end} ?`)) return;
    try {
      const res = await axios.delete(`${API}/portal/paie/runs/${r.id}`, { headers: portalHeaders() });
      if (!isDemoResponse(res)) toast.success("Feuille de temps supprimée");
      load(); onChanged?.();
    } catch (e) { err(e); }
  };

  const closeDetail = useCallback(() => { setOpenId(null); load(); onChanged?.(); }, [load, onChanged]);

  if (openId) {
    return <TimesheetDetail runId={openId} summary={summary} primaryColor={primaryColor} onBack={closeDetail} />;
  }

  return (
    <SectionCard title="Feuilles de temps" icon={CalendarClock} primaryColor={primaryColor} testId="paie-portal-timesheets"
      action={(
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="border rounded-lg px-3 py-2 text-sm" data-testid="paie-portal-run-year">
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {!creating && (
            <button type="button" onClick={() => setCreating(true)} className={`${addBtnCls} flex-1`} style={{ backgroundColor: primaryColor }} data-testid="paie-portal-run-add">
              <Plus className="w-4 h-4" /> Nouvelle feuille de temps
            </button>
          )}
        </div>
      )}>
      {creating && <RunForm runs={runs || []} defaultFrequency={summary.default_frequency} primaryColor={primaryColor} onDone={(r) => { setCreating(false); setOpenId(r.id); }} onCancel={() => setCreating(false)} />}
      {runs === null ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-300 mx-auto" /></div>
        : runs.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8" data-testid="paie-portal-runs-empty">
            Aucune feuille de temps en {year}. Créez-en une pour saisir les heures de vos employés.
          </p>
        ) : (
          <ul className="divide-y" data-testid="paie-portal-runs-list">
            {runs.map((r) => (
              <li key={r.id} className="py-3 flex items-center gap-3" data-testid={`paie-portal-run-row-${r.id}`}>
                <button type="button" onClick={() => setOpenId(r.id)} className="flex-1 min-w-0 text-left" data-testid={`paie-portal-run-open-${r.id}`}>
                  <div className="font-medium text-gray-900 flex items-center gap-2 flex-wrap">
                    {r.period_start} → {r.period_end}
                    <ClientStatus status={r.client_status} testId={`paie-portal-run-status-${r.id}`} />
                    {r.docs.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{r.docs.length} document(s)</span>}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">Paie le {fmtDate(r.pay_date)} · {FREQ_LABELS[r.frequency]} · {r.nb_employees} employé(s){r.note ? ` · ${r.note}` : ""}</div>
                </button>
                {r.client_status === "open" && r.source === "portal" && (
                  <button type="button" onClick={() => remove(r)} className={deleteBtnCls} title="Supprimer" data-testid={`paie-portal-run-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>
                )}
                <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
              </li>
            ))}
          </ul>
        )}
    </SectionCard>
  );
}
